import { useCallback, useEffect, useRef, useState } from 'react'
import type { ResizeHandleProps } from './use-resizable-columns'

// =============================================================================
// Types
// =============================================================================

/**
 * Configuration for the resizable row hook.
 *
 * Heights are pixels, not percentages — unlike a column split, a row split has
 * no bounded parent to take a percentage of, and the pixel the pointer lands
 * on is the height the reader actually gets.
 */
export interface UseResizableRowConfig {
  /** Starting height in pixels (clamped into [minHeight, maxHeight]) */
  initialHeight: number
  /** Smallest height the row may be dragged to, in pixels */
  minHeight: number
  /** Largest height the row may be dragged to, in pixels */
  maxHeight: number
  /** Pixels moved per Arrow key press (default: 24) */
  keyboardStep?: number
  /** Callback when the height changes */
  onHeightChange?: (height: number) => void
}

/** Return type for the useResizableRow hook */
export interface UseResizableRowReturn {
  /** Current row height in pixels */
  height: number
  /** Props for the drag handle sitting under the resizable row */
  getResizeHandleProps: () => ResizeHandleProps
  /** Reset to the initial height */
  reset: () => void
}

// =============================================================================
// Utilities
// =============================================================================

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

// =============================================================================
// Hook Implementation
// =============================================================================

/**
 * Hook for managing the height of a resizable row, dragged by a handle
 * directly beneath it.
 *
 * The Y-axis twin of `useResizableColumns`, and deliberately not a mode of it:
 * that hook divides a fixed 100% between N sibling columns, whereas a row
 * splitter grows one region and lets the page below reflow, so it has no
 * "other side" to take width from and no percentage to conserve.
 *
 * Drag (pointer, so mouse/touch/pen alike) and Arrow Up/Down are both
 * supported; give the handle `touch-action: none` (`touch-none`) so a touch
 * drag resizes instead of scrolling the page.
 *
 * @example
 * ```tsx
 * const { height, getResizeHandleProps } = useResizableRow({
 *   initialHeight: 480,
 *   minHeight: 240,
 *   maxHeight: 1200,
 * })
 *
 * return (
 *   <>
 *     <div style={{ height }}>…the region being resized…</div>
 *     <div className="h-2 cursor-row-resize touch-none" {...getResizeHandleProps()} />
 *   </>
 * )
 * ```
 */
export function useResizableRow(
  config: UseResizableRowConfig,
): UseResizableRowReturn {
  const {
    initialHeight,
    minHeight,
    maxHeight,
    keyboardStep = 24,
    onHeightChange,
  } = config

  const [height, setHeight] = useState(() =>
    clamp(initialHeight, minHeight, maxHeight),
  )

  // The authoritative current height for anything that reads it outside of
  // render: every writer goes through `updateHeight`, so a handler can act on
  // the latest value instead of the one its closure captured. Without it two
  // Arrow presses delivered in one task (an OS key-repeat burst) both read the
  // same snapshot and the divider moves a single step.
  const heightRef = useRef(height)

  // Refs for drag state
  const isDraggingRef = useRef(false)
  const startYRef = useRef(0)
  const startHeightRef = useRef(height)

  // Stored cleanup for the active drag, so an unmount mid-drag still removes
  // the document listeners and restores the body styles.
  const cleanupRef = useRef<(() => void) | null>(null)

  const updateHeight = useCallback(
    (next: number) => {
      heightRef.current = next
      setHeight(next)
      onHeightChange?.(next)
    },
    [onHeightChange],
  )

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault()

      isDraggingRef.current = true
      startYRef.current = e.clientY
      startHeightRef.current = heightRef.current

      // Keep receiving moves once the pointer leaves the handle (and route
      // touch moves here rather than scrolling, with touch-action: none).
      const handle = e.currentTarget
      if (handle instanceof Element) {
        try {
          handle.setPointerCapture(e.pointerId)
        } catch {
          // Capture is best-effort; document listeners still track the drag.
        }
      }

      const handlePointerMove = (moveEvent: PointerEvent) => {
        if (!isDraggingRef.current) return
        const deltaY = moveEvent.clientY - startYRef.current
        updateHeight(
          clamp(startHeightRef.current + deltaY, minHeight, maxHeight),
        )
      }

      const cleanup = () => {
        isDraggingRef.current = false
        cleanupRef.current = null
        document.removeEventListener('pointermove', handlePointerMove)
        document.removeEventListener('pointerup', handlePointerEnd)
        document.removeEventListener('pointercancel', handlePointerEnd)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }

      const handlePointerEnd = () => {
        cleanup()
      }

      cleanupRef.current = cleanup

      document.addEventListener('pointermove', handlePointerMove)
      document.addEventListener('pointerup', handlePointerEnd)
      document.addEventListener('pointercancel', handlePointerEnd)
      document.body.style.cursor = 'row-resize'
      document.body.style.userSelect = 'none'
    },
    [maxHeight, minHeight, updateHeight],
  )

  // Cleanup on unmount - handles the case where the component unmounts mid-drag
  useEffect(() => {
    return () => {
      cleanupRef.current?.()
    }
  }, [])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return
      e.preventDefault()
      const delta = e.key === 'ArrowUp' ? -keyboardStep : keyboardStep
      updateHeight(clamp(heightRef.current + delta, minHeight, maxHeight))
    },
    [keyboardStep, maxHeight, minHeight, updateHeight],
  )

  const getResizeHandleProps = useCallback(
    (): ResizeHandleProps => ({
      onPointerDown: handlePointerDown,
      onKeyDown: handleKeyDown,
    }),
    [handlePointerDown, handleKeyDown],
  )

  const reset = useCallback(() => {
    updateHeight(clamp(initialHeight, minHeight, maxHeight))
  }, [initialHeight, maxHeight, minHeight, updateHeight])

  return { height, getResizeHandleProps, reset }
}
